// Sample Data admin page — view per-table sample row counts, wipe with
// type-WIPE-to-confirm guard, reload via offline seeder.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

type Summary = {
  tables: Array<{ table: string; sample_count: number }>;
  total_sample_rows: number;
};

const TABLE_LABEL: Record<string, string> = {
  'crew_roster':              'Crew roster',
  'weekly_payroll_entry':     'Weekly payroll',
  'permit_crossings':         'Permit crossings',
  'bead_baba_cert':           'BABA certifications',
  'bead_speed_test':          'BSL speed tests',
  'bead_evidence':            'Service-ready evidence',
  'bead_obligation_ai_drafts':'AI-drafted compliance filings',
  'bead_vendor':              'Vendors',
};

const TABLE_DESCRIPTION: Record<string, string> = {
  'crew_roster':              'Sample workers with classifications + wage rates',
  'weekly_payroll_entry':     'Sample weekly hours per worker per project',
  'permit_crossings':         'Sample railroad / waterway / highway crossings',
  'bead_baba_cert':           'Sample manufacturer self-certs + waivers',
  'bead_speed_test':          'Sample BMC-format speed tests on real BSLs',
  'bead_evidence':            'Sample geotagged service-ready photos',
  'bead_obligation_ai_drafts':'AI drafts created during compliance autopilot demos',
  'bead_vendor':              'Sample vendors used by sample BABA certs',
};

export default function SampleDataPage() {
  const qc = useQueryClient();
  const { data: summary, isLoading } = useQuery<Summary>({
    queryKey: ['sample-data-summary'],
    queryFn: () => api.get('/admin/sample-data/summary'),
    refetchInterval: 5000,  // refresh while user watches
  });

  const [confirmText, setConfirmText] = useState('');
  const [wipeResult, setWipeResult] = useState<any>(null);

  const wipeMut = useMutation({
    mutationFn: () => api.post('/admin/sample-data/wipe', { confirm: 'WIPE' }),
    onSuccess: (res: any) => {
      setWipeResult(res);
      setConfirmText('');
      qc.invalidateQueries({ queryKey: ['sample-data-summary'] });
    },
  });

  if (isLoading) return <div className="card"><div className="card__body">Loading…</div></div>;

  const total = summary?.total_sample_rows ?? 0;
  const isPristine = total === 0;

  return (
    <div style={{ maxWidth: 900 }}>
      <div className="page__header">
        <div>
          <h1 className="page__title">Sample Data</h1>
          <div className="page__subtitle">
            Test fixtures for compliance autopilot demos. Wipe before going to production.
          </div>
        </div>
      </div>

      {/* Status banner */}
      {isPristine ? (
        <div style={{
          background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6,
          padding: '12px 16px', marginBottom: 16, color: '#065f46', fontWeight: 600,
        }}>
          ✓ Production-clean — no sample rows in any table
        </div>
      ) : (
        <div style={{
          background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 6,
          padding: '12px 16px', marginBottom: 16, color: '#92400e',
        }}>
          <div style={{ fontWeight: 700, marginBottom: 2 }}>● {total.toLocaleString()} sample rows in DB</div>
          <div style={{ fontSize: 12 }}>Sample data is being used to drive demo flows. Wipe before going live with real operations.</div>
        </div>
      )}

      {/* Per-table breakdown */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 16, overflow: 'hidden' }}>
        <div style={{ background: '#f8fafc', padding: '8px 14px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 13 }}>
          Sample data by table
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
              <th style={{ textAlign: 'left',  padding: '6px 12px', fontWeight: 600, color: '#475569' }}>Table</th>
              <th style={{ textAlign: 'left',  padding: '6px 12px', fontWeight: 600, color: '#475569' }}>What it holds</th>
              <th style={{ textAlign: 'right', padding: '6px 12px', fontWeight: 600, color: '#475569', width: 90 }}>Sample rows</th>
            </tr>
          </thead>
          <tbody>
            {summary?.tables.map((t) => (
              <tr key={t.table} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '6px 12px', fontWeight: 500 }}>
                  {TABLE_LABEL[t.table] ?? t.table}
                  <div style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'ui-monospace, monospace' }}>{t.table}</div>
                </td>
                <td style={{ padding: '6px 12px', color: '#64748b' }}>{TABLE_DESCRIPTION[t.table] ?? '—'}</td>
                <td style={{ padding: '6px 12px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: t.sample_count > 0 ? 700 : 400, color: t.sample_count > 0 ? '#0f172a' : '#94a3b8' }}>
                  {t.sample_count.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Wipe controls */}
      {!isPristine && (
        <div style={{ background: '#fff', border: '2px solid #fecaca', borderRadius: 6, padding: '14px 18px', marginBottom: 16 }}>
          <h2 style={{ margin: 0, marginBottom: 6, fontSize: 14, fontWeight: 700, color: '#991b1b' }}>Wipe all sample data</h2>
          <div style={{ fontSize: 12, color: '#475569', marginBottom: 10 }}>
            Hard-deletes every row across the {summary?.tables.length ?? 0} tagged tables. Real production data
            (awards, projects, locations, milestones, obligations, plant settings, cost catalog) is
            untouched. Action is logged with timestamp and user.
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder='Type WIPE to enable'
              style={{ width: 180, padding: '6px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 3, fontFamily: 'ui-monospace, monospace' }}
            />
            <button
              disabled={confirmText !== 'WIPE' || wipeMut.isPending}
              onClick={() => wipeMut.mutate()}
              style={{
                padding: '6px 16px', fontSize: 13, fontWeight: 600,
                background: confirmText === 'WIPE' ? '#dc2626' : '#fecaca',
                color: '#fff', border: 0, borderRadius: 4,
                cursor: confirmText === 'WIPE' ? 'pointer' : 'not-allowed',
              }}
            >
              {wipeMut.isPending ? 'Wiping…' : '🗑 Wipe sample data'}
            </button>
            {wipeMut.isError && (
              <span style={{ color: '#b91c1c', fontSize: 12 }}>Error: {(wipeMut.error as any)?.message}</span>
            )}
          </div>

          {wipeResult && (
            <div style={{ marginTop: 12, padding: 10, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 4, fontSize: 11 }}>
              <div style={{ fontWeight: 600, color: '#065f46', marginBottom: 4 }}>
                ✓ Wiped {wipeResult.total_deleted} rows at {new Date(wipeResult.wiped_at).toLocaleString()}
              </div>
              <table style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace' }}>
                <tbody>
                  {Object.entries(wipeResult.deleted ?? {}).map(([t, n]) => (
                    <tr key={t}>
                      <td style={{ padding: '1px 8px 1px 0' }}>{t}</td>
                      <td style={{ padding: '1px 0', textAlign: 'right' }}>{n as number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Reload note */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '12px 16px', fontSize: 12, color: '#475569' }}>
        <strong>Reload sample data:</strong> currently runs offline via{' '}
        <code style={{ background: '#fff', padding: '1px 6px', borderRadius: 3, border: '1px solid #cbd5e1' }}>
          /tmp/ffexec.sh /c/tmp/ff_pm_build/sample_data_seeder.py
        </code>
        . The seeder is idempotent — safe to run anytime.
      </div>

      <div style={{ marginTop: 16, fontSize: 10, color: '#94a3b8', textAlign: 'right' }}>
        Tables flagged with <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 2 }}>is_sample</code> column.
        Real production data added to these tables (when you go live) will use <code style={{ background: '#f1f5f9', padding: '1px 4px', borderRadius: 2 }}>is_sample = false</code> and survive the wipe.
      </div>
    </div>
  );
}
